import React from 'react';
import { motion } from 'framer-motion';
import { Drone, MapPin, Navigation, Video, Compass } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';

const highlights = [
  {
    icon: <Drone className="w-6 h-6 text-blue-600" />,
    title: 'Tour aéreo 360°',
    description: 'Recorridos inmersivos con drones para entender el entorno completo del desarrollo y sus accesos.'
  },
  {
    icon: <Navigation className="w-6 h-6 text-purple-600" />,
    title: 'Mapeo de amenidades',
    description: 'Identificamos vialidades, equipamiento urbano y puntos de interés alrededor de cada propiedad.'
  },
  {
    icon: <Video className="w-6 h-6 text-indigo-600" />,
    title: 'Video narrado',
    description: 'Narrativa guiada que explica ventajas, distancias y oportunidades de inversión en minutos.'
  }
];

const zones = [
  { name: 'Corredor Reforma', description: 'Panorámicas de corporativos, parques urbanos y conectividad al transporte masivo.' },
  { name: 'Condominios del poniente', description: 'Vista aérea de condominios, amenidades privadas y áreas verdes protegidas.' },
  { name: 'Polos emergentes', description: 'Cobertura de zonas con alta plusvalía proyectada y nuevos desarrollos habitacionales.' }
];

const VirtualTours = () => {
  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-purple-50">
        <Header />
        <div className="container mx-auto px-4 py-12 max-w-6xl">
        <motion.section
          className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-3xl p-10 text-white shadow-2xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex flex-col lg:flex-row items-center gap-10">
            <div className="flex-1 space-y-4">
              <p className="uppercase tracking-[0.3em] text-sm text-white/80">Experiencias inmersivas</p>
              <h1 className="text-4xl md:text-5xl font-extrabold">
                Tour Virtuales Aéreos por las Zonas Clave
              </h1>
              <p className="text-lg text-white/90">
                Muestra a tus clientes la experiencia completa antes de visitar: vuelos con drones, mapas interactivos y narración estratégica que resalta accesos, amenidades y desarrollo urbano.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-blue-700 font-semibold rounded-full hover:bg-blue-50 transition-colors"
                >
                  Regresar al listado
                </Link>
                <a
                  href="mailto:ventas@codigoenblanco.com"
                  className="inline-flex items-center justify-center px-6 py-3 border border-white/60 rounded-full font-semibold hover:bg-white/10 transition-colors"
                >
                  Solicitar producción
                </a>
              </div>
            </div>
            <div className="flex-1 w-full">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=1200&q=80"
                  alt="Vista aérea de la ciudad"
                  className="w-full h-72 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 text-white">
                  <p className="text-sm uppercase tracking-[0.3em]">Vista aérea</p>
                  <p className="text-2xl font-bold">Contexto urbano extendido</p>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        <motion.section
          className="grid md:grid-cols-3 gap-6 mt-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          {highlights.map((highlight, idx) => (
            <div key={idx} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mb-4">
                {highlight.icon}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{highlight.title}</h3>
              <p className="text-gray-600">{highlight.description}</p>
            </div>
          ))}
        </motion.section>

        <motion.section
          className="mt-16 bg-white rounded-3xl shadow-xl border border-gray-100 p-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex flex-col lg:flex-row gap-10">
            <div className="flex-1">
              <p className="uppercase text-sm font-semibold text-blue-600 tracking-[0.3em] mb-3">Cobertura aérea</p>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Narrativa territorial y plusvalía</h2>
              <p className="text-gray-600 mb-6">
                Cada video integra mapas 3D, datos de movilidad y planos maestros para mostrar la conectividad real de la zona. Ideal para inversionistas y futuros residentes que desean tener certeza antes de cerrar.
              </p>
              <div className="space-y-4">
                {zones.map((zone, idx) => (
                  <div key={idx} className="flex gap-4 p-4 bg-gray-50 rounded-2xl border border-gray-100">
                    <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-700 font-bold flex items-center justify-center">
                      {idx + 1}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-blue-500" />
                        {zone.name}
                      </h3>
                      <p className="text-gray-600">{zone.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex-1">
              <div className="relative h-full rounded-2xl overflow-hidden bg-gradient-to-br from-blue-500 to-purple-600 text-white flex flex-col justify-between p-8">
                <Compass className="w-12 h-12 text-white/70" />
                <div>
                  <p className="text-sm uppercase tracking-[0.4em] text-white/70">Atención personalizada</p>
                  <h3 className="text-3xl font-bold mt-2">Planeamos vuelos a la medida de cada desarrollo</h3>
                  <p className="text-white/80 mt-4">
                    Coordinamos permisos, logística y edición en 72 horas para presentar un masterplan completo en cualquier junta comercial.
                  </p>
                </div>
                <a
                  href="mailto:contacto@codigoenblanco.com"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-blue-700 font-semibold rounded-full hover:bg-blue-50 transition-colors mt-6"
                >
                  Agendar vuelo exploratorio
                </a>
              </div>
            </div>
          </div>
        </motion.section>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default VirtualTours;
